import { useState } from "react";
import { Link } from "react-router";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { ArrowRight, BookOpen, Mail, Send } from "lucide-react";
import { JOIN_FORM_URL } from "../config/links";

export default function ForTheRestOfUs() {
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success">("idle");

  const handleStayUpdated = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would integrate with your email service
    console.log("Stay updated email:", email);
    setSubmitStatus("success");
    setEmail("");
    setTimeout(() => setSubmitStatus("idle"), 3000);
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="For the Rest of Us - Unpatterned"
        description="A publishing series dedicated to translating mathematics, software, data, AI, and institutional systems into rigorous, accessible knowledge."
        keywords={[
          "for the rest of us",
          "data for the rest of us",
          "accessible knowledge",
          "complex systems",
          "publishing",
          "intellectual press",
        ]}
        url="https://unpatterned.org/for-the-rest-of-us"
      />
      <Header />

      {/* Hero Section - Minimal & Strong */}
      <section className="pt-40 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-['DM_Serif_Display',serif] text-5xl md:text-7xl text-[#050e1d] mb-8 leading-tight">
            For the Rest of Us
          </h1>
          <p className="font-['Manrope',sans-serif] text-2xl md:text-3xl text-[#050e1d]/80 mb-6 leading-relaxed">
            Making complex systems and topics legible for everyone else.
          </p>
          <p className="font-['Manrope',sans-serif] text-lg md:text-xl text-[#050e1d]/60 mb-12 leading-relaxed max-w-3xl mx-auto">
            A publishing series from Unpatterned dedicated to translating mathematics, software, data, AI, and institutional systems into rigorous, accessible knowledge — working closely with experts and leaders in the field.
          </p>
          <a
            href="#current-titles"
            className="inline-flex items-center gap-2 bg-[#184b9d] text-white px-8 py-4 rounded-lg font-['Manrope',sans-serif] font-semibold text-lg hover:bg-[#1e8eaf] transition-all group"
          >
            Explore the Series
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* The Why */}
      <section className="py-24 px-6 bg-gradient-to-b from-[rgba(24,75,157,0.02)] to-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-12">
            Complexity shapes everyday life.
          </h2>
          <div className="space-y-8 font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed">
            <p>
              Complex ideas shape our lives — even when we do not see them.
            </p>
            <p>
              Data. Algorithms. Institutions. Economic models. Technological systems.
            </p>
            <p>
              These forces influence everyday decisions, from how loans are approved to how information circulates.
            </p>
            <p className="font-medium text-[#050e1d] text-2xl pt-8">
              "For the Rest of Us" is a publishing initiative dedicated to translating complex systems into clear, rigorous, and accessible knowledge — without oversimplification.
            </p>
            <p className="pt-4">
              It is not "dumbing down."<br />
              It is intellectual bridge-building.
            </p>
          </div>
        </div>
      </section>

      {/* The Philosophy */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-8">
            Clarity is not simplification.
          </h2>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 mb-12 leading-relaxed">
            We believe:
          </p>
          <div className="space-y-8">
            <div className="pl-6 border-l-2 border-[#184b9d]">
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed">
                Depth should not be confined to specialists.
              </p>
            </div>
            <div className="pl-6 border-l-2 border-[#184b9d]">
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed">
                Mathematical and technological ideas can be made legible without losing rigor.
              </p>
            </div>
            <div className="pl-6 border-l-2 border-[#184b9d]">
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed">
                Underrepresented knowledge traditions deserve structured amplification.
              </p>
            </div>
          </div>
          <div className="mt-16 pt-12 border-t border-[rgba(5,14,26,0.1)]">
            <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 mb-8 leading-relaxed">
              This series bridges:
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]">
                  Theory and lived experience
                </p>
              </div>
              <div className="text-center">
                <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]">
                  Technical systems and everyday decisions
                </p>
              </div>
              <div className="text-center">
                <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]">
                  Global knowledge traditions and contemporary challenges
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current & Upcoming Titles */}
      <section id="current-titles" className="py-24 px-6 bg-gradient-to-b from-white to-[rgba(24,75,157,0.02)]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-16 text-center">
            Current & Upcoming Work
          </h2>
          
          {/* Featured: Data for the Rest of Us */}
          <div className="bg-white border-2 border-[#184b9d] rounded-2xl p-12 mb-16 shadow-lg">
            <div className="mb-4">
              <span className="inline-block bg-[#184b9d] text-white px-4 py-1 rounded-full text-sm font-['Manrope',sans-serif] font-medium">
                Available Now
              </span>
            </div>
            <h3 className="font-['DM_Serif_Display',serif] text-3xl md:text-4xl text-[#050e1d] mb-4">
              Data for the Rest of Us
            </h3>
            <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70 mb-8 leading-relaxed">
              Connecting data, models, and everyday life. Making visible the mathematical structures that quietly shape our world — from credit scores to content algorithms.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-[#184b9d] text-white px-8 py-3 rounded-lg font-['Manrope',sans-serif] font-semibold hover:bg-[#1e8eaf] transition-colors">
                Learn More
              </button>
              <button className="bg-white border-2 border-[#184b9d] text-[#184b9d] px-8 py-3 rounded-lg font-['Manrope',sans-serif] font-semibold hover:bg-[rgba(24,75,157,0.05)] transition-colors">
                Preorder
              </button>
            </div>
          </div>

          {/* Topics of Interest */}
          <div className="mb-12">
            <h4 className="font-['Manrope',sans-serif] text-2xl text-[#050e1d] mb-4 text-center font-semibold">
              Topics We're Exploring
            </h4>
            <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 mb-8 text-center max-w-3xl mx-auto">
              We're interested in series related to these and similar topics:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-[rgba(5,14,26,0.1)] rounded-xl p-6 text-center hover:border-[#184b9d] transition-all">
                <h5 className="font-['Manrope',sans-serif] text-lg text-[#050e1d] font-semibold">
                  Geospatial & Location
                </h5>
              </div>
              <div className="bg-white border border-[rgba(5,14,26,0.1)] rounded-xl p-6 text-center hover:border-[#184b9d] transition-all">
                <h5 className="font-['Manrope',sans-serif] text-lg text-[#050e1d] font-semibold">
                  Software
                </h5>
              </div>
              <div className="bg-white border border-[rgba(5,14,26,0.1)] rounded-xl p-6 text-center hover:border-[#184b9d] transition-all">
                <h5 className="font-['Manrope',sans-serif] text-lg text-[#050e1d] font-semibold">
                  Artificial Intelligence
                </h5>
              </div>
              <div className="bg-white border border-[rgba(5,14,26,0.1)] rounded-xl p-6 text-center hover:border-[#184b9d] transition-all">
                <h5 className="font-['Manrope',sans-serif] text-lg text-[#050e1d] font-semibold">
                  Knowledge from the Global South
                </h5>
              </div>
              <div className="bg-white border border-[rgba(5,14,26,0.1)] rounded-xl p-6 text-center hover:border-[#184b9d] transition-all">
                <h5 className="font-['Manrope',sans-serif] text-lg text-[#050e1d] font-semibold">
                  Institutions
                </h5>
              </div>
              <div className="bg-white border border-[rgba(5,14,26,0.1)] rounded-xl p-6 text-center hover:border-[#184b9d] transition-all">
                <h5 className="font-['Manrope',sans-serif] text-lg text-[#050e1d] font-semibold">
                  Entrepreneurship
                </h5>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bridge Back to Unpatterned */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-8">
            From Practice to Page
          </h2>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 mb-8 leading-relaxed">
            "For the Rest of Us" grows out of Unpatterned's core work:
          </p>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#184b9d] rounded-full mt-2"></div>
              <div>
                <h4 className="font-['Manrope',sans-serif] font-semibold text-lg text-[#050e1d] mb-2">
                  Monthly Themes
                </h4>
                <p className="font-['Manrope',sans-serif] text-[#050e1d]/70">
                  Deep dives into focused topics
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#184b9d] rounded-full mt-2"></div>
              <div>
                <h4 className="font-['Manrope',sans-serif] font-semibold text-lg text-[#050e1d] mb-2">
                  Applied Labs
                </h4>
                <p className="font-['Manrope',sans-serif] text-[#050e1d]/70">
                  Real problems under real constraints
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#184b9d] rounded-full mt-2"></div>
              <div>
                <h4 className="font-['Manrope',sans-serif] font-semibold text-lg text-[#050e1d] mb-2">
                  Structured Reasoning
                </h4>
                <p className="font-['Manrope',sans-serif] text-[#050e1d]/70">
                  Systems, first principles, second-order thinking
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#184b9d] rounded-full mt-2"></div>
              <div>
                <h4 className="font-['Manrope',sans-serif] font-semibold text-lg text-[#050e1d] mb-2">
                  Global Collaboration
                </h4>
                <p className="font-['Manrope',sans-serif] text-[#050e1d]/70">
                  Diverse perspectives and knowledge traditions
                </p>
              </div>
            </div>
          </div>
          <div className="bg-[rgba(24,75,157,0.05)] rounded-xl p-8 text-center">
            <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80 leading-relaxed">
              Ideas are tested in dialogue.<br />
              Refined in practice.<br />
              Then published for broader access.
            </p>
          </div>
        </div>
      </section>

      {/* For Writers, Thinkers & Storytellers */}
      <section className="py-24 px-6 bg-gradient-to-b from-[rgba(24,75,157,0.02)] to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <BookOpen size={48} className="text-[#184b9d] mx-auto mb-6" />
            <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-6">
              For Writers, Thinkers & Storytellers
            </h2>
            <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed max-w-3xl mx-auto mb-8">
              If you are interested in simplifying complex topics — or know how to translate complexity without losing depth — we would like to hear from you.
            </p>
          </div>
          <div className="space-y-6 mb-12">
            <div className="bg-white rounded-xl p-6 border border-[rgba(5,14,26,0.1)]">
              <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                Translate complexity without distortion
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[rgba(5,14,26,0.1)]">
              <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                Connect systems to lived experience
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[rgba(5,14,26,0.1)]">
              <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                Bridge continents and disciplines
              </p>
            </div>
          </div>
          <div className="bg-white border-2 border-[#184b9d] rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-['Manrope',sans-serif] text-2xl text-[#050e1d] mb-4 font-semibold">
              Share Your Idea
            </h3>
            <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              We support authors who can make rigorous ideas accessible. If you are working on a manuscript or series aligned with this vision, we welcome conversations.
            </p>
            <a
              href={JOIN_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#184b9d] text-white px-8 py-4 rounded-lg font-['Manrope',sans-serif] font-semibold hover:bg-[#1e8eaf] transition-colors"
            >
              <Send size={20} />
              Submit Your Proposal
            </a>
          </div>
        </div>
      </section>

      {/* Stay Updated */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Mail size={48} className="text-[#184b9d] mx-auto mb-6" />
          <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-6">
            Stay Updated
          </h2>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 mb-8 leading-relaxed">
            Be the first to know about new releases, author events, and early access opportunities.
          </p>
          {submitStatus === "success" ? (
            <div className="bg-[rgba(24,75,157,0.1)] text-[#184b9d] px-6 py-4 rounded-lg font-['Manrope',sans-serif] inline-block">
              Thank you for subscribing!
            </div>
          ) : (
            <form onSubmit={handleStayUpdated} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-6 py-4 border-2 border-[rgba(5,14,26,0.2)] rounded-lg font-['Manrope',sans-serif] focus:outline-none focus:border-[#184b9d] transition-colors"
              />
              <button
                type="submit"
                className="bg-[#184b9d] text-white px-8 py-4 rounded-lg font-['Manrope',sans-serif] font-semibold hover:bg-[#1e8eaf] transition-colors whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
